import { defineStore } from "pinia";
import { parseBoolean } from "@spoferan/spoferan-ts-core";
import { authOriginalTokenCookieName, authOriginalUserNameCookieName, authStateCookieName, authTokenCookieName } from "../config/cookies.mjs";
import { useRuntimeConfig, useNuxtApp, useRoute, useCookie, navigateTo } from "#app";
import { NativeApp } from "../services/index.mjs";
export const useAuthStore = defineStore("auth", {
  state: () => ({
    // Whether the authentication state for the token has been initialized.
    // Note that the user might wasn't already loaded at this point but only the auth token and user id is accessible.
    initialized: false,
    // Whether the authentication state for the user has been initialized.
    userLoaded: false,
    // States whether the user is logged in or not
    // This is safe to be used on the server and client side
    loggedIn: false,
    // The following is only set on CLIENT SIDE when the user is logged in, i.e. loggedIn === true
    // Do not use this on the server side as these will always be null regardless of the user's authentication state to improve cache performance.
    // Therefore, all access to $auth on the following props must be protected with 'client-only' tags to prevent hydration issues.
    user: null,
    // The authentication token of the user
    token: null,
    // The name of the user before impersonation
    originalUserName: null,
    // Whether the authenticated user is currently impersonating another user.
    // This functionality is only enabled for admins.
    isImpersonating: false
  }),
  getters: {
    // The bearer token version of the user to send to the API in the Authorization header.
    bearerToken: (state) => state.token ? `Bearer ${state.token}` : null,
    // The decoded token data
    tokenData: (state) => {
      if (!state.token) {
        return null;
      }
      const jsonPayload = window.atob(state.token);
      return JSON.parse(jsonPayload);
    },
    // The date when the authentication state of the currently authenticated user expires and the user needs to re-authenticate.
    authExpiration: (state) => state.tokenData?.exp ? new Date(state.tokenData.exp) : null,
    // The id of the user prop for easy access.
    // We extract it out of the token to access it without requiring loading the user data.
    id: (state) => state.tokenData ? state.tokenData.uid : null
  },
  actions: {
    /**
     * Initializes the authentication state for SSR.
     * This only checks for the authentication state as all other cookies are not available on SSR.
     */
    async initServer() {
      this.loggedIn = parseBoolean(this.getAuthCookieValue(authStateCookieName));
    },
    /**
     * Initializes the authentication state for CSR.
     * On the client side, we can access all cookies, so we can also set the user and token.
     */
    async initClient() {
      await this.initServer();
      const authToken = this.getAuthCookieValue(authTokenCookieName);
      if (!authToken && this.loggedIn) {
        this.setLoggedOutState();
      }
      this.$patch({
        initialized: true,
        token: authToken
      });
      if (authToken) {
        await this.fetchUser();
        this.isImpersonating = !!this.getAuthCookieValue(authOriginalTokenCookieName);
        if (this.isImpersonating) {
          this.originalUserName = this.getAuthCookieValue(authOriginalUserNameCookieName) ?? "Admin";
        }
      }
      this.userLoaded = true;
    },
    getAuthProvider() {
      const { $device } = useNuxtApp();
      return $device.isNative ? "native" : "web";
    },
    async login(credentials) {
      return new Promise(async (resolve, reject) => {
        const config = useRuntimeConfig();
        const { $apiFetch } = useNuxtApp();
        try {
          const data = await $apiFetch(`${config.public.apiUrl}/v1/auth/login`, {
            method: "POST",
            silent: true,
            body: {
              provider: this.getAuthProvider(),
              ...credentials
            },
            headers: {
              "Authorization": this.bearerToken
            }
          });
          await this.setLoggedInState(data.user, data.token);
          this.unsetImpersonatingState();
          resolve();
        } catch (e) {
          console.error(e);
          this.setLoggedOutState();
          reject(e);
        }
      });
    },
    async logout(method = void 0, withRedirect = false) {
      if (!this.loggedIn || !this.token) {
        this.setLoggedOutState();
        return;
      }
      const config = useRuntimeConfig();
      const { $apiFetch, $analytics, $spoferanUrl } = useNuxtApp();
      await $apiFetch(`${config.public.apiUrl}/v1/auth/logout`, {
        method: "POST",
        headers: {
          "Authorization": this.bearerToken
        }
      });
      if (method) {
        $analytics.trackEvent("logout", {
          method
        });
      }
      if (config.public.isSpoferanDomain) {
        const route = useRoute();
        this.loggedIn = false;
        await navigateTo({ path: "/login", query: withRedirect ? { redirect: route.fullPath } : {} });
        this.setLoggedOutState();
      } else {
        this.unsetAuthCookies();
        window.location.href = $spoferanUrl("login") + (withRedirect ? "?redirect=" + encodeURIComponent(window.location.href) : "");
      }
    },
    /**
     * Refreshes the user data from the API.
     */
    async fetchUser() {
      const config = useRuntimeConfig();
      const { $apiFetch } = useNuxtApp();
      if (!this.token) {
        return null;
      }
      try {
        const data = await $apiFetch(`${config.public.apiUrl}/v1/auth/user`, {
          params: {
            provider: this.getAuthProvider()
          },
          headers: {
            "Authorization": this.bearerToken
          }
        });
        await this.setLoggedInState(data.user, this.token);
        return data.user;
      } catch (e) {
        if (e.statusCode === 401) {
          this.setLoggedOutState();
        }
      }
      return null;
    },
    async setUser(user) {
      const { $i18n } = useNuxtApp();
      this.loggedIn = true;
      this.user = user;
      const userLocale = this.user.locale ? this.user.locale.split("-")[0] : null;
      if (userLocale && userLocale !== $i18n.locale.value) {
        await $i18n.setLocale(userLocale);
      }
    },
    async setUserToken(token) {
      this.token = token;
      return this.fetchUser();
    },
    /*+
     * Persists the logged in state for the user as cookies.
     */
    async setLoggedInState(user, token) {
      await this.setUser(user);
      this.token = token;
      this.setAuthCookieValue(authStateCookieName, true, this.authExpiration);
      this.setAuthCookieValue(authTokenCookieName, token, this.authExpiration);
      await NativeApp.setUserId(user.id);
    },
    setLoggedOutState() {
      this.unsetAuthCookies();
      this.loggedIn = false;
      this.user = null;
      this.token = null;
      this.unsetImpersonatingState();
      NativeApp.setUserId(null);
    },
    unsetAuthCookies() {
      this.unsetAuthCookieValue(authStateCookieName);
      this.unsetAuthCookieValue(authTokenCookieName);
      this.unsetImpersonatingCookies();
    },
    getAuthCookieValue(name) {
      return useCookie(name, { domain: this.getAuthCookieDomain() }).value;
    },
    setAuthCookieValue(name, value, tokenExpiration = null) {
      useCookie(name, { expires: tokenExpiration ?? void 0, domain: this.getAuthCookieDomain() }).value = value;
    },
    unsetAuthCookieValue(name) {
      useCookie(name, { domain: this.getAuthCookieDomain() }).value = null;
    },
    /**
     * Gets the domain on which to set and read the auth cookies.
     */
    getAuthCookieDomain() {
      const config = useRuntimeConfig();
      const appUrl = config.public.appUrl;
      const spoferanDomain = config.public.domain;
      const supportsSubdomains = appUrl.includes(spoferanDomain) && !spoferanDomain.includes("localhost");
      const cookieDomain = supportsSubdomains ? `.${spoferanDomain.split("://").slice(-1)[0]}` : appUrl.split("://")[1].split(":")[0];
      return cookieDomain;
    },
    /**
     * Impersonate as another user for admins.
     */
    async impersonate(userId) {
      if (!this.user) {
        throw Error("Cannot impersonate another user when not logged in.");
      }
      if (!this.user.is_admin) {
        throw Error("Only admins can impersonate as another user.");
      }
      const config = useRuntimeConfig();
      const { $apiFetch, $device } = useNuxtApp();
      const originalToken = this.token;
      const originalUserName = this.user.display_name;
      const data = await $apiFetch(`${config.public.apiUrl}/v1/auth/user/${userId}/impersonate`, {
        method: "POST",
        headers: {
          "Authorization": this.bearerToken
        }
      });
      await this.setLoggedInState(data.user, data.token);
      this.setAuthCookieValue(authOriginalTokenCookieName, originalToken, this.authExpiration);
      this.setAuthCookieValue(authOriginalUserNameCookieName, originalUserName, this.authExpiration);
      this.originalUserName = originalUserName;
      this.isImpersonating = true;
      const appUrl = config.public.appUrl;
      const spoferanUrl = config.public.spoferanUrl;
      if (appUrl !== spoferanUrl) {
        window.location.href = spoferanUrl;
      }
    },
    async stopImpersonate() {
      const originalToken = this.getAuthCookieValue(authOriginalTokenCookieName);
      if (this.isImpersonating && originalToken) {
        const config = useRuntimeConfig();
        const { $apiFetch } = useNuxtApp();
        await $apiFetch(`${config.public.apiUrl}/v1/auth/logout`, {
          method: "POST",
          headers: {
            "Authorization": this.bearerToken
          }
        });
        await this.setUserToken(originalToken);
        this.unsetImpersonatingState();
      } else {
        this.unsetImpersonatingState();
        await this.logout("stop_impersonate");
      }
    },
    unsetImpersonatingState() {
      if (this.isImpersonating) {
        this.unsetImpersonatingCookies();
      }
      this.isImpersonating = false;
      this.originalUserName = this.user ? this.user.display_name : null;
    },
    unsetImpersonatingCookies() {
      if (this.isImpersonating) {
        this.unsetAuthCookieValue(authOriginalTokenCookieName);
        this.unsetAuthCookieValue(authOriginalUserNameCookieName);
      }
    }
  }
});
